import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getPermissionsManagerUrlToUse } from "@dealer-center/ddc-pages-user-permissions-manager-fetch";
import type {
  CreateNrtRecordRequest,
  DeleteNrtRecordRequest,
  NrtRecord,
  NrtRecordWire,
  NrtRecordsResponseBody,
  UpdateNrtRecordRequest
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import { walkCursorPages } from "./cursorPagination";

// Same-origin base every handler in this Lambda is mounted under.
export const NRT_LAMBDA_BASE_PATH = "/invsup/nxlambda/nrtDatabaseFilterTool";

// Every list consumer (the query itself, plus the create/update mutations
// that need to invalidate it) shares this key, so it only lives in one place.
export const NRT_RECORDS_LIST_QUERY_KEY = [
  "ddc-pages-nrt-database-filter-tool",
  "list"
] as const;

const asText = (value: unknown): string | null =>
  value === null || value === undefined ? null : String(value);

// The one place wire data becomes a trustworthy NrtRecord: getAllRecords casts
// rather than converts, so an N-typed attribute arrives as a number.
export const normalizeNrtRecord = (item: NrtRecordWire): NrtRecord => ({
  accountId: String(item.accountId),
  id: String(item.id),
  caseNumber: String(item.caseNumber),
  classification: (item.classification ?? null) as NrtRecord["classification"],
  createdBy: String(item.createdBy),
  dateCreated: asText(item.dateCreated),
  expirationDate: asText(item.expirationDate),
  lastAccessed: asText(item.lastAccessed),
  map: asText(item.map),
  notes: asText(item.notes),
  providerId: String(item.providerId),
  select: asText(item.select),
  status: item.status as NrtRecord["status"],
  tags: asText(item.tags),
  type: (item.type ?? null) as NrtRecord["type"]
});

// API Gateway caps limit at 500 — largest page size, fewest round trips.
const maxPageSize = 500;

// nextCursor decides the page count, so the only failure left to guard is a
// cursor that never advances.
export const repeatedCursorMessage =
  "getAllRecords returned a repeated pagination cursor, so the record list " +
  "cannot be completed without looping.";

// Same-origin path so it resolves in every env; `signal` is React Query's, so
// cancellation aborts the in-flight page.
const getNrtRecordsPage = async (
  cursor?: string,
  signal?: AbortSignal
): Promise<NrtRecordsResponseBody> => {
  const params = new URLSearchParams({ limit: String(maxPageSize) });
  if (cursor) {
    params.set("cursor", cursor);
  }

  const res = await fetch(
    `${NRT_LAMBDA_BASE_PATH}/getAllRecords?${params.toString()}`,
    { signal }
  );

  if (!res.ok) {
    throw new Error(
      `Failed to fetch NRT records: ${res.status} ${res.statusText}`
    );
  }

  return (await res.json()) as NrtRecordsResponseBody;
};

// No server-side search/filtering, so this loops every page and hands back
// the full list for the page's client-side search/sort/pagination.
export const getAllNrtRecords = async (
  signal?: AbortSignal
): Promise<NrtRecord[]> =>
  walkCursorPages(async cursor => {
    const { items, pagination } = await getNrtRecordsPage(cursor, signal);
    return {
      items: items.map(normalizeNrtRecord),
      nextCursor: pagination.nextCursor
    };
  }, repeatedCursorMessage);

export function useNrtRecords() {
  return useQuery({
    queryKey: NRT_RECORDS_LIST_QUERY_KEY,
    queryFn: ({ signal }) => getAllNrtRecords(signal),
    retry: 1,
    // refetchOnMount already covers the "fresh on every page load" AC. Focus is
    // excluded because each refetch re-walks every page.
    refetchOnWindowFocus: false,
    staleTime: 0
  });
}

// Reads `{ message }` or `{ errors: [{ field: message }] }`; null if neither shape matches.
export const extractApiErrorMessage = (body: unknown): string | null => {
  const message = (body as { message?: unknown })?.message;
  if (typeof message === "string") return message;

  const errors = (body as { errors?: unknown })?.errors;
  if (Array.isArray(errors)) {
    const messages = errors
      .filter(
        (error): error is Record<string, unknown> =>
          typeof error === "object" && error !== null
      )
      .flatMap(error => Object.values(error))
      .filter((value): value is string => typeof value === "string");
    if (messages.length > 0) return messages.join(", ");
  }
  return null;
};

// Prefers the API's JSON error message, else falls back to the status line.
export const readErrorMessage = async (
  response: Response,
  fallbackPrefix: string
): Promise<string> => {
  try {
    const body: unknown = await response.json();
    const message = extractApiErrorMessage(body);
    if (message) return message;
  } catch {
    // Not JSON — fall through to the status line.
  }
  return `${fallbackPrefix}: ${response.status} ${response.statusText}`;
};

// The status matters to callers: a 403 means the local permission state is stale.
export interface NrtRequestError extends Error {
  status: number;
}

export const isNrtRequestError = (error: unknown): error is NrtRequestError =>
  error instanceof Error &&
  typeof (error as NrtRequestError).status === "number";

// A plain Error with a status attached rather than an Error subclass, so the
// existing `rejects.toThrow(message)` assertions keep working unchanged.
const buildRequestError = (message: string, status: number): NrtRequestError =>
  Object.assign(new Error(message), { status });

const submitNrtRecord = async (
  path: string,
  method: "POST" | "PUT" | "DELETE",
  body: unknown
): Promise<NrtRecordWire> => {
  const res = await fetch(`${NRT_LAMBDA_BASE_PATH}/${path}`, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });

  if (!res.ok) {
    throw buildRequestError(
      await readErrorMessage(res, "Request failed"),
      res.status
    );
  }

  return (await res.json()) as NrtRecordWire;
};

export const createNrtRecord = (
  request: CreateNrtRecordRequest
): Promise<NrtRecord> =>
  submitNrtRecord("createRecord", "POST", request).then(normalizeNrtRecord);

export const updateNrtRecord = (
  request: UpdateNrtRecordRequest
): Promise<NrtRecord> =>
  submitNrtRecord("updateRecord", "PUT", request).then(normalizeNrtRecord);

export const deleteNrtRecord = (
  request: DeleteNrtRecordRequest
): Promise<NrtRecord> =>
  submitNrtRecord("deleteRecord", "DELETE", request).then(normalizeNrtRecord);

// FR: on success the list must auto-refresh — invalidating this query is
// what makes that happen, since RecordsTable renders straight from it.
const useNrtRecordMutation = <TRequest>(
  mutationFn: (request: TRequest) => Promise<NrtRecord>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NRT_RECORDS_LIST_QUERY_KEY });
    }
  });
};

export function useCreateNrtRecord() {
  return useNrtRecordMutation(createNrtRecord);
}

export function useUpdateNrtRecord() {
  return useNrtRecordMutation(updateNrtRecord);
}

// Exported so a consumer can invalidate this query after a 403 (FR-019) —
// the locally-held role set is then known to be stale.
export const NRT_USER_PERMISSION_GROUPS_QUERY_KEY = [
  "ddc-pages-nrt-database-filter-tool",
  "user-permission-groups"
] as const;

// The shared UPM endpoint returns 200 even on failure and double-JSON-encodes
// its body — see contracts/upm-permission-lookup.md.
const PERMISSION_LOOKUP_FAILED_MESSAGE =
  "Could not determine your NRT permission groups.";

export const getUserPermissionGroups = async (
  userId: string,
  signal?: AbortSignal
): Promise<string[]> => {
  const res = await fetch(getPermissionsManagerUrlToUse(userId), { signal });

  if (!res.ok) {
    throw buildRequestError(
      await readErrorMessage(res, "Request failed"),
      res.status
    );
  }

  let outerBody: unknown;
  try {
    outerBody = await res.json();
  } catch {
    throw buildRequestError(PERMISSION_LOOKUP_FAILED_MESSAGE, 502);
  }

  // An upstream failure body isn't re-stringified the same way, so this
  // catches both "not a string" and a bad second JSON.parse below.
  if (typeof outerBody !== "string") {
    throw buildRequestError(PERMISSION_LOOKUP_FAILED_MESSAGE, 502);
  }

  let groups: unknown;
  try {
    groups = JSON.parse(outerBody);
  } catch {
    throw buildRequestError(PERMISSION_LOOKUP_FAILED_MESSAGE, 502);
  }

  if (!Array.isArray(groups)) {
    throw buildRequestError(PERMISSION_LOOKUP_FAILED_MESSAGE, 502);
  }

  return groups as string[];
};

// `userId` is required: the shared endpoint's path includes it, so `enabled`
// gates the query until the session lookup resolves one.
export function useNrtUserPermissionGroups(userId: string | null | undefined) {
  return useQuery({
    queryKey: NRT_USER_PERMISSION_GROUPS_QUERY_KEY,
    queryFn: ({ signal }) => getUserPermissionGroups(userId as string, signal),
    enabled: !!userId,
    // No retry/refetch-on-focus: an affordance must never appear then be
    // withdrawn (FR-016).
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 600000 // 10 mins
  });
}

export function useDeleteNrtRecord() {
  return useNrtRecordMutation(deleteNrtRecord);
}
